import { ContractStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { Button, Grid, GridItem, IconClose, TextField, IconSearch } from 'uikit';

import { debounce } from 'debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

export const AgreementsRegistryToolbar = ({
    createAgreement,
    disabled = false,
    isLoadingInProgress = false,
    handleSearch,
    hasActiveFilters,
    isDisabledByProjectStatus,
    statusContract,
}: {
    createAgreement: (state: boolean) => void;
    disabled?: boolean;
    isLoadingInProgress?: boolean;
    handleSearch: (value: any) => void;
    hasActiveFilters: boolean;
    isDisabledByProjectStatus: boolean;
    statusContract?: ContractStatusEnum;
}) => {
    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId, false);
    const { additionalAgreement: additionalAgreementRules } = useAccess();

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [searchValue, setSearchValue] = useState('');

    const debouncedSearch = useMemo(() => debounce(handleSearch, 500), [handleSearch]);

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;

    const handleChangeSearchValue = useCallback(
        ({ value }) => {
            setSearchValue(value);
            debouncedSearch({ value });
        },
        [debouncedSearch],
    );
    const handleClearSearchValue = useCallback(() => {
        setSearchValue('');
        debouncedSearch('');
    }, [debouncedSearch]);

    const createAgreementsHandler = useCallback(() => {
        createAgreement?.(true);

        sendGpnAnalyticsEvent({
            componentId: 'rim_create DS button_pressed',
            projectId,
            contractId: projectData?.contractId,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        });
    }, [
        createAgreement,
        projectData?.contractId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
    ]);

    useEffect(() => {
        if (hasActiveFilters) {
            setSearchValue('');
        }
    }, [hasActiveFilters]);

    return (
        <Grid cols="2" colGap="xl" rowGap="l">
            <GridItem style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                    placeholder="Поиск доп. соглашений"
                    value={searchValue}
                    onChange={handleChangeSearchValue}
                    width="full"
                    size="m"
                    leftSide={IconSearch}
                    disabled={disabled}
                />

                {searchValue && (
                    <Button
                        view="ghost"
                        iconLeft={IconClose}
                        form="round"
                        size="xs"
                        onlyIcon
                        style={{ position: 'relative', left: -30, zIndex: 10 }}
                        onClick={handleClearSearchValue}
                    />
                )}
            </GridItem>
            <GridItem style={{ display: 'flex', justifyContent: 'right' }}>
                <Button
                    label="Создать доп. соглашение"
                    onClick={createAgreementsHandler}
                    loading={isLoadingInProgress}
                    disabled={
                        isDisabledByProjectStatus || !isContractStatusWork || !additionalAgreementRules?.canCreate
                    }
                />
            </GridItem>
        </Grid>
    );
};

AgreementsRegistryToolbar.displayName = 'AgreementsRegistryToolbar';
